import { Check, Paperclip, Undo2, Upload } from 'lucide-react';

import { usePermission } from '../../../../auth/AuthContext';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import {
  ATTRITION_REVIEW,
  type AttritionDocumentReviewStatus,
  type AttritionRequestDetail,
  type AttritionRequestDocument,
} from '../attritionTypes';

/** A document's state on a profile — `pending` is legal here, never on an index surface. */
const REVIEW_TONE: Record<AttritionDocumentReviewStatus, StatusTone> = {
  [ATTRITION_REVIEW.notSubmitted]: 'inactive',
  [ATTRITION_REVIEW.forReview]: 'pending',
  [ATTRITION_REVIEW.approved]: 'active',
  [ATTRITION_REVIEW.returned]: 'blocked',
};

/**
 * A file's action glyph, at the 32px floor a table's action gutter takes — the 201 file
 * Documents panel's own control, for its reason: a 40px button is taller than the line it acts on.
 */
const GLYPH_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 ' +
  'hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent';

/**
 * The request's checklist, one row per document — the 201 file Documents panel's shape: the
 * CHECKLIST is listed, not the files, so a document nobody has submitted is a row saying so.
 *
 * Each row states the document's review state, who reviews it, the submitted file (its name OPENS
 * the preview) and, on a return, the reviewer's reason. **Upload** is the uploader's
 * (`attrition-requests.update`); **Approve** and **Return** are the line's OWN reviewer's — drawn
 * only where the checklist names the caller AND they hold `attrition-requests.review`, because the
 * server refuses either alone.
 *
 * **No Edit on this card** — its state moves by actions, not typing: one of the cards the profile
 * rule lists as legitimately having none.
 */
export function AttritionDocumentsCard({
  record,
  busy,
  uploadPermission = 'attrition-requests.update',
  onUpload,
  onOpen,
  onReview,
}: {
  record: AttritionRequestDetail;
  busy: boolean;
  /** The key the SURFACE uploads under — HR's `attrition-requests.update`, or Self Service's `my-attrition-requests.upload`. */
  uploadPermission?: string;
  onUpload: (document: AttritionRequestDocument) => void;
  onOpen: (document: AttritionRequestDocument) => void;
  /** Absent on Self Service — an employee reviews nothing, so no Approve or Return is drawn at all. */
  onReview?: (document: AttritionRequestDocument, decision: 'approve' | 'return') => void;
}) {
  const canUpload = usePermission(uploadPermission);
  const canReview = usePermission('attrition-requests.review') && onReview != null;

  const closedReason = record.isOpen
    ? null
    : !record.isActive
      ? 'This request is deleted. Restore it before acting on it.'
      : `This request is ${record.statusLabel.toLowerCase()}.`;

  const rows: PreviewRow[] = record.documents.map((document) => {
    const uploadReason =
      closedReason ??
      (document.reviewStatus === ATTRITION_REVIEW.approved ? 'This document is approved and can no longer be replaced.' : null);
    const reviewable = document.reviewStatus === ATTRITION_REVIEW.forReview;

    return {
      label: document.name,
      value: (
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <StatusText tone={REVIEW_TONE[document.reviewStatus]}>{document.reviewStatusLabel}</StatusText>
              <span className="text-xs text-slate-500">
                Reviewed by {document.reviewerName ?? 'an account no longer listed'}
                {document.isMine ? ' (you)' : ''}
              </span>
            </span>

            <span className="flex shrink-0 items-center">
              {/* Hidden, not disabled, from a reader who cannot do it — disabled only for STATE, with why. */}
              {canReview && document.isMine && reviewable ? (
                <>
                  <Tooltip label={closedReason ?? 'Approve'}>
                    <button
                      type="button"
                      id={`attrition-document-approve-${document.id}`}
                      onClick={() => onReview?.(document, 'approve')}
                      disabled={busy || closedReason != null}
                      aria-label={`Approve ${document.name}`}
                      className={GLYPH_BUTTON + ' hover:text-emerald-700'}
                    >
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                  <Tooltip label={closedReason ?? 'Return'}>
                    <button
                      type="button"
                      id={`attrition-document-return-${document.id}`}
                      onClick={() => onReview?.(document, 'return')}
                      disabled={busy || closedReason != null}
                      aria-label={`Return ${document.name}`}
                      className={GLYPH_BUTTON + ' hover:text-red-600'}
                    >
                      <Undo2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </Tooltip>
                </>
              ) : null}

              {canUpload ? (
                <Tooltip label={uploadReason ?? (document.fileName ? 'Replace' : 'Upload')}>
                  <button
                    type="button"
                    id={`attrition-document-upload-${document.id}`}
                    onClick={() => onUpload(document)}
                    disabled={busy || uploadReason != null}
                    aria-label={`${document.fileName ? 'Replace' : 'Upload'} ${document.name}`}
                    className={GLYPH_BUTTON + ' hover:text-slate-700'}
                  >
                    <Upload className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ) : null}
            </span>
          </div>

          {document.description ? <span className="text-xs text-slate-400">{document.description}</span> : null}

          {document.fileName ? (
            <span className="flex min-w-0 items-center gap-2">
              <Paperclip className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              <button
                type="button"
                onClick={() => onOpen(document)}
                disabled={busy}
                className="min-w-0 truncate text-left font-medium text-slate-800 hover:underline disabled:text-slate-400"
              >
                {document.fileName}
              </button>
              {document.submittedAt != null ? (
                <span className="shrink-0 text-xs text-slate-400">
                  {document.submittedByName ?? '—'} ·{' '}
                  <time dateTime={toIso(document.submittedAt)} className="font-mono">
                    {formatDateTime(document.submittedAt)}
                  </time>
                </span>
              ) : null}
            </span>
          ) : (
            // Not an em dash: the request has no file for this document, which is what the reader scans for.
            <span className="text-slate-400">Not submitted</span>
          )}

          {document.reviewStatus === ATTRITION_REVIEW.returned && document.reviewRemarks ? (
            <span className="rounded-md bg-red-50 px-2 py-1 text-xs text-red-800">
              Returned by {document.reviewedByName ?? '—'}: {document.reviewRemarks}
            </span>
          ) : document.reviewStatus === ATTRITION_REVIEW.approved && document.reviewedAt != null ? (
            <span className="text-xs text-slate-500">
              Approved by {document.reviewedByName ?? '—'} ·{' '}
              <time dateTime={toIso(document.reviewedAt)} className="font-mono">
                {formatDateTime(document.reviewedAt)}
              </time>
              {document.reviewRemarks ? ` — ${document.reviewRemarks}` : ''}
            </span>
          ) : null}
        </div>
      ),
    };
  });

  return (
    <ProfileCard id="attrition-request-profile-documents" title="Document checklist" count={record.documents.length}>
      <PreviewTable variant="table-display" rows={rows} />
    </ProfileCard>
  );
}
